import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import {
  type IStep,
  messagesState,
  threadHistoryState,
  useAuth,
  useChatData,
  useChatInteract,
  useChatMessages,
  useConfig
} from '@chainlit/react-client';

import { type Message, conversationsApi } from '@/api/conversations';
import Alert from '@/components/Alert';
import { TaskList } from '@/components/Tasklist';
import ChatToolbar from '@/components/chat/ChatToolbar';
import ChatFooter from '@/components/chat/Footer';
import MessagesContainer from '@/components/chat/MessagesContainer';
import ScrollContainer from '@/components/chat/ScrollContainer';
import WelcomeScreen from '@/components/chat/WelcomeScreen';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Translator } from 'components/i18n';
import { useTranslation } from 'components/i18n/Translator';

import { useUpload } from '@/hooks/useUpload';
import { useLayoutMaxWidth } from 'hooks/useLayoutMaxWidth';

import { IAttachment, attachmentsState, resumeConversationIdState } from 'state/chat';

// 把 DB 裡的歷史訊息轉成主介面 Messages 元件的 IStep。
// 助理訊息若帶有 analysis（分析過程），在它前面補一個已結束的 tool step
// 「分析過程」（含子步驟），呈現方式與即時問答結束後的收合區塊一致。
function toISteps(m: Message): IStep[] {
  const out: IStep[] = [];
  if (m.role === 'assistant' && m.analysis && m.analysis.log) {
    const parentId = `${m.id}-analysis`;
    out.push({
      id: parentId,
      name: '分析過程',
      type: 'tool',
      output: m.analysis.log,
      showInput: false,
      createdAt: m.created_at,
      start: m.created_at,
      end: m.created_at,
      steps: (m.analysis.subs || []).map(([name, content], i) => ({
        id: `${parentId}-${i}`,
        parentId,
        name,
        type: 'tool' as const,
        output: content,
        showInput: false,
        createdAt: m.created_at,
        start: m.created_at,
        end: m.created_at
      }))
    });
  }
  out.push({
    id: m.id,
    name: m.role === 'user' ? 'User' : 'Assistant',
    type: m.role === 'user' ? 'user_message' : 'assistant_message',
    output: m.content,
    createdAt: m.created_at
  });
  return out;
}

const isWelcomeMessage = (m: IStep) =>
  (m.metadata as Record<string, unknown> | undefined)?.kind === 'welcome';

export default function ChatPage() {
  const { config } = useConfig();
  const { user } = useAuth();
  const setAttachments = useSetRecoilState(attachmentsState);
  const setThreads = useSetRecoilState(threadHistoryState);
  const autoScrollRef = useRef(true);
  const { error, disabled, callFn } = useChatData();
  const { uploadFile } = useChatInteract();
  const uploadFileRef = useRef(uploadFile);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const layoutMaxWidth = useLayoutMaxWidth();
  const { threadId, messages } = useChatMessages();
  const setMessages = useSetRecoilState(messagesState);
  const setResumeConvId = useSetRecoilState(resumeConversationIdState);

  // 歷史對話接續模式：/chat?conv=:id
  // 抓舊訊息灌進主介面的訊息流；發話仍走 Chainlit WebSocket，
  // MessageComposer 會在 metadata 帶 conversation_id 讓後端接續該對話。
  const convId = searchParams.get('conv');

  useEffect(() => {
    setResumeConvId(convId);
    if (!convId) return;
    let cancelled = false;
    conversationsApi
      .getMessages(convId)
      .then((d) => {
        if (cancelled) return;
        setMessages(d.messages.flatMap(toISteps));
      })
      .catch((e) =>
        toast.error(e instanceof Error ? e.message : '載入對話失敗')
      );
    return () => {
      cancelled = true;
    };
  }, [convId]);

  // 歷史對話模式下，過濾掉本 session on_chat_start 發送的歡迎訊息
  // （它可能在灌入舊訊息之後才透過 socket 抵達，會出現在對話最下方）
  useEffect(() => {
    if (!convId) return;
    if (messages.some(isWelcomeMessage)) {
      setMessages((prev) => prev.filter((m) => !isWelcomeMessage(m)));
    }
  }, [convId, messages]);

  const fileSpec = useMemo(
    () => ({
      max_size_mb: config?.features?.spontaneous_file_upload?.max_size_mb || 500,
      max_files: config?.features?.spontaneous_file_upload?.max_files || 20,
      accept: config?.features?.spontaneous_file_upload?.accept || {
        'application/*': [],
        'audio/*': [],
        'image/*': [],
        'text/*': [],
        'video/*': []
      }
    }),
    [config]
  );

  const onFileUpload = useCallback(
    (payloads: File[]) => {
      const attachements: IAttachment[] = payloads.map((file) => {
        const id = uuidv4();
        const { xhr, promise } = uploadFileRef.current(file, (progress) => {
          setAttachments((prev) =>
            prev.map((a) => (a.id === id ? { ...a, uploadProgress: progress } : a))
          );
        });

        promise
          .then((res) => {
            setAttachments((prev) =>
              prev.map((a) =>
                a.id === id
                  ? { ...a, serverId: res.id, uploaded: true, uploadProgress: 100, cancel: undefined }
                  : a
              )
            );
          })
          .catch((error) => {
            toast.error(
              `${t('chat.fileUpload.errors.failed')} ${file.name}: ${
                typeof error === 'object' && error !== null ? error.message ?? error : error
              }`
            );
            setAttachments((prev) => prev.filter((a) => a.id !== id));
          });

        return {
          id,
          type: file.type,
          name: file.name,
          size: file.size,
          uploadProgress: 0,
          cancel: () => {
            toast.info(`${t('chat.fileUpload.errors.cancelled')} ${file.name}`);
            xhr.abort();
            setAttachments((prev) => prev.filter((a) => a.id !== id));
          },
          remove: () => {
            setAttachments((prev) => prev.filter((a) => a.id !== id));
          }
        };
      });
      setAttachments((prev) => prev.concat(attachements));
    },
    [uploadFile]
  );

  const onFileUploadError = useCallback((error: string) => toast.error(error), []);

  const upload = useUpload({
    spec: fileSpec,
    onResolved: onFileUpload,
    onError: onFileUploadError,
    options: { noClick: true }
  });

  const enableAttachments = !disabled && config?.features?.spontaneous_file_upload?.enabled;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* 工具列 */}
      <ChatToolbar />

      {/* 聊天主體 */}
      <div
        {...(enableAttachments ? upload.getRootProps({ className: 'dropzone' }) : {})}
        onBlur={undefined}
        onFocus={undefined}
        className="flex w-full h-full flex-col relative overflow-hidden"
      >
        {enableAttachments && <input id="#upload-drop-input" {...upload.getInputProps()} />}

        {error && (
          <div className="w-full mx-auto my-2">
            <Alert className="mx-2" id="session-error" variant="error">
              <Translator path="common.status.error.serverConnection" />
            </Alert>
          </div>
        )}

        <ErrorBoundary>
          <ScrollContainer
            autoScrollUserMessage={config?.features?.user_message_autoscroll}
            autoScrollAssistantMessage={config?.features?.assistant_message_autoscroll}
            autoScrollRef={autoScrollRef}
          >
            <div
              className="flex flex-col mx-auto w-full flex-grow p-4"
              style={{ maxWidth: layoutMaxWidth }}
            >
              <TaskList isMobile={true} />
              <WelcomeScreen
                fileSpec={fileSpec}
                onFileUpload={onFileUpload}
                onFileUploadError={onFileUploadError}
                autoScrollRef={autoScrollRef}
              />
              <MessagesContainer navigate={navigate} />
            </div>
          </ScrollContainer>
          <div
            className="flex flex-col mx-auto w-full p-4 pt-0"
            style={{ maxWidth: layoutMaxWidth }}
          >
            <ChatFooter
              fileSpec={fileSpec}
              onFileUpload={onFileUpload}
              onFileUploadError={onFileUploadError}
              autoScrollRef={autoScrollRef}
            />
          </div>
        </ErrorBoundary>
      </div>
    </div>
  );
}
